import React from 'react';
import { Box, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  FDataGrid,
  FGridMissingCell,
  FGridMoneyCell,
  FGridPercentCell,
  FGridStatusChip,
  isMissingGridValue,
} from '../grid';
import { ActivityProgrammeRow } from './activityprogramme.models';

interface Props {
  rows: ActivityProgrammeRow[];
  loading?: boolean;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
  onNotifyResponsible?: (row: ActivityProgrammeRow) => void;
  onNotifyIncompleteResponsible?: (row: ActivityProgrammeRow) => void;
  onNotifyAllIncomplete?: () => void;
  onNotifyAllResponsible?: () => void;
  onComplete?: (row: ActivityProgrammeRow) => void;
  onCertify?: (row: ActivityProgrammeRow) => void;
  onReturn?: (row: ActivityProgrammeRow) => void;
  onBulkComplete?: () => void;
  onBulkCertify?: () => void;
}

const QuarterCell = (params: GridRenderCellParams) => (
  <Box sx={{ width: '100%', textAlign: 'center' }}>{params.value || ''}</Box>
);

export default class ProgrammeReviewGrid extends React.PureComponent<Props> {
  private incompleteCount = () =>
    (this.props.rows || []).filter((row) => row.missing.length > 0).length;

  private columns = (): GridColDef[] => [
    { field: 'code', headerName: 'Code', width: 145, renderCell: FGridMissingCell, cellClassName: 'programme-cell-master' },
    { field: 'name', headerName: 'Activity', minWidth: 280, flex: 1, renderCell: FGridMissingCell, cellClassName: 'programme-cell-master' },
    { field: 'costcentercode', headerName: 'Cost Center', width: 140, cellClassName: 'programme-cell-governed' },
    { field: 'domaine', headerName: 'Domaine', minWidth: 220, flex: 1, renderCell: FGridMissingCell, cellClassName: 'programme-cell-master' },
    { field: 'resultat', headerName: 'Résultat attendu', minWidth: 260, flex: 1, renderCell: FGridMissingCell, cellClassName: 'programme-cell-master' },
    { field: 'indicateur', headerName: 'Indicateur', minWidth: 250, flex: 1, renderCell: FGridMissingCell, cellClassName: 'programme-cell-master' },
    { field: 'cible', headerName: 'Cible', width: 110, renderCell: FGridMissingCell, cellClassName: 'programme-cell-governed' },
    { field: 't1', headerName: 'T1', width: 65, renderCell: QuarterCell },
    { field: 't2', headerName: 'T2', width: 65, renderCell: QuarterCell },
    { field: 't3', headerName: 'T3', width: 65, renderCell: QuarterCell },
    { field: 't4', headerName: 'T4', width: 65, renderCell: QuarterCell },
    { field: 'statecode', headerName: 'Plan State', width: 145, renderCell: FGridStatusChip, cellClassName: 'programme-cell-governed' },
    { field: 'taskcount', headerName: 'Tasks', width: 95, type: 'number', cellClassName: 'programme-cell-computed' },
    { field: 'budgetlinecount', headerName: 'Budget Lines', width: 115, type: 'number', cellClassName: 'programme-cell-computed' },
    { field: 'plannedamount', headerName: 'Planned Amount', width: 155, renderCell: FGridMoneyCell, cellClassName: 'programme-cell-computed' },
    { field: 'revisedamount', headerName: 'Revised Amount', width: 155, renderCell: FGridMoneyCell, cellClassName: 'programme-cell-computed' },
    { field: 'variancepercent', headerName: 'Écart %', width: 110, renderCell: FGridPercentCell, cellClassName: 'programme-cell-computed' },
    {
      field: 'readiness',
      headerName: 'Readiness',
      width: 145,
      valueGetter: (_value, row: ActivityProgrammeRow) => row.missing.length,
      renderCell: (params) => {
        const row = params.row as ActivityProgrammeRow;
        return (
          <Chip
            size="small"
            color={row.missing.length === 0 ? 'success' : 'warning'}
            label={row.missing.length === 0 ? 'Ready' : `${row.missing.length} issue(s)`}
          />
        );
      },
      cellClassName: 'programme-cell-computed',
    },
    { field: 'commentaires', headerName: 'Commentaires', minWidth: 220, flex: 1, renderCell: FGridMissingCell },
    { field: 'responsable', headerName: 'Responsable', minWidth: 220, flex: 1, renderCell: FGridMissingCell },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 190,
      getActions: (params) => {
        const row = params.row as ActivityProgrammeRow;
        const incomplete = row.missing.length > 0;
        const state = row.statecode;
        return [
          <GridActionsCellItem key="notify" icon={<SendIcon fontSize="small" />} label="Notify responsible" onClick={() => this.props.onNotifyResponsible?.(row)} />,
          <GridActionsCellItem key="missing" icon={<WarningAmberIcon fontSize="small" />} label="Notify incomplete" disabled={!incomplete} onClick={() => this.props.onNotifyIncompleteResponsible?.(row)} />,
          <GridActionsCellItem key="complete" icon={<CheckCircleOutlineIcon fontSize="small" />} label="Mark complete" disabled={incomplete || state === 'COMPLETE' || state === 'CERTIFIED'} onClick={() => this.props.onComplete?.(row)} />,
          <GridActionsCellItem key="certify" icon={<VerifiedOutlinedIcon fontSize="small" />} label="Certify" disabled={incomplete || state !== 'COMPLETE'} onClick={() => this.props.onCertify?.(row)} />,
          <GridActionsCellItem key="return" icon={<UndoOutlinedIcon fontSize="small" />} label="Return" disabled={state === 'RETURNED'} onClick={() => this.props.onReturn?.(row)} />,
        ];
      },
    },
  ];

  private toolbarActions = () => {
    const selected = this.props.selectedIds?.length || 0;
    const incomplete = this.incompleteCount();
    const total = this.props.rows?.length || 0;
    return [
      { key: 'complete-selected', label: 'Complete Selected', icon: <CheckCircleOutlineIcon />, disabled: selected === 0, onClick: () => this.props.onBulkComplete?.() },
      { key: 'certify-selected', label: 'Certify Selected', icon: <VerifiedOutlinedIcon />, disabled: selected === 0, onClick: () => this.props.onBulkCertify?.() },
      { key: 'notify-incomplete', label: 'Notify Incomplete', icon: <WarningAmberIcon />, disabled: incomplete === 0, onClick: () => this.props.onNotifyAllIncomplete?.() },
      { key: 'notify-all', label: 'Notify All', icon: <NotificationsActiveIcon />, disabled: total === 0, onClick: () => this.props.onNotifyAllResponsible?.() },
    ];
  };

  render() {
    return (
      <FDataGrid
        title="Programme d'Activités — Horizontal Review"
        subtitle={`${this.props.rows?.length || 0} Activity Plans • ${this.incompleteCount()} incomplete`}
        rows={this.props.rows || []}
        columns={this.columns()}
        loading={this.props.loading}
        height={650}
        getRowId={(row: ActivityProgrammeRow) => row.id}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={(this.props.selectedIds || []) as any}
        onRowSelectionModelChange={(model: any) =>
          this.props.onSelectionChange?.(Array.from(model as any).map(Number))
        }
        toolbarActions={this.toolbarActions()}
        showQuickFilter
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        getCellClassName={(params) =>
          isMissingGridValue(params.row, params.field) ? 'programme-cell-missing' : ''
        }
        getRowClassName={(params) =>
          params.row.missing.length > 0 ? 'programme-row-incomplete' : 'programme-row-ready'
        }
        sx={{
          '& .programme-cell-master': { bgcolor: 'info.lighter' },
          '& .programme-cell-computed': { bgcolor: 'success.lighter', fontWeight: 600 },
          '& .programme-cell-governed': { bgcolor: 'action.hover' },
          '& .programme-cell-missing': { bgcolor: 'error.lighter', color: 'error.dark', fontWeight: 600 },
          '& .programme-row-incomplete': { boxShadow: 'inset 4px 0 0 0', boxShadowColor: 'warning.main' },
          '& .programme-row-ready': { boxShadow: 'inset 4px 0 0 0', boxShadowColor: 'success.main' },
        }}
      />
    );
  }
}
